import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import api, { PreorderCustomerSuggestion } from '../../services/api';

interface CustomerNameAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  /** Appelé quand un client connu est choisi (nom + dernier téléphone connu). */
  onSelect: (customer: PreorderCustomerSuggestion) => void;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
}

/**
 * Saisie du nom du client à la prise de précommande, avec proposition des
 * clients déjà venus (historique des précommandes).
 *
 * Le champ reste LIBRE : un nouveau client se tape normalement, la liste n'est
 * qu'une aide. Elle évite surtout les doublons d'orthographe (« Nancy » /
 * « nancy ») qui éclatent l'historique d'un même client.
 *
 * Les flèches et Entrée sont gérées : les caisses ont un vrai clavier.
 */
export default function CustomerNameAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = 'Nom et prenom',
  disabled = false,
  autoFocus = false,
}: CustomerNameAutocompleteProps) {
  const [suggestions, setSuggestions] = useState<PreorderCustomerSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Un client tout juste choisi ne doit pas rouvrir la liste sur son propre nom.
  const justPicked = useRef(false);
  // La liste s'affiche AU-DESSUS du champ quand le clavier tactile occupe la
  // place en dessous (signalé le 19/09/2026 : au magasin, les propositions
  // etaient cachees derriere le clavier, et les refermer les faisait perdre).
  const [boite, setBoite] = useState<{ left: number; width: number; top?: number; bottom?: number } | null>(
    null,
  );

  const search = useCallback(async (query: string) => {
    if (query.trim().length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }
    setIsLoading(true);
    try {
      const results = await api.suggestPreorderCustomers(query, 8);
      setSuggestions(results);
      setShowDropdown(results.length > 0);
      setSelectedIndex(-1);
    } catch {
      // Sans réseau (marché, coupure), la saisie libre doit continuer de marcher :
      // pas de message d'erreur, on n'affiche simplement aucune proposition.
      setSuggestions([]);
      setShowDropdown(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    onChange(next);
    justPicked.current = false;
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if (!justPicked.current) search(next);
    }, 250);
  };

  const handleSelect = (customer: PreorderCustomerSuggestion) => {
    justPicked.current = true;
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    onChange(customer.name);
    onSelect(customer);
    setShowDropdown(false);
    setSuggestions([]);
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || suggestions.length === 0) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        // Sans ligne surlignée on laisse passer : Entrée valide le formulaire.
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          e.preventDefault();
          handleSelect(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
    }
  };

  /**
   * Choisit le cote ou dessiner la liste.
   *
   * On ne se contente pas de « le clavier est-il ouvert ? » : il est
   * DEPLACABLE, l'operateur peut l'avoir mis en haut. On mesure donc sa
   * position reelle et on bascule au-dessus du champ seulement s'il mange
   * vraiment la place en dessous.
   */
  const choisirPlacement = useCallback(() => {
    const champ = inputRef.current;
    if (!champ) return;
    const r = champ.getBoundingClientRect();
    const HAUTEUR_LISTE = 288; // max-h-72
    let basDisponible = window.innerHeight - r.bottom;

    // Le clavier est DEPLACABLE : on lit sa position reelle, pas seulement
    // « est-il ouvert ? ». Il ne gene que s'il est SOUS le champ.
    const clavier = document.querySelector('[data-virtual-keyboard]') as HTMLElement | null;
    if (clavier) {
      const k = clavier.getBoundingClientRect();
      if (k.height > 0 && k.top > r.top) {
        basDisponible = Math.min(basDisponible, Math.max(0, k.top - r.bottom));
      }
    }

    const dessus = basDisponible < Math.min(HAUTEUR_LISTE, 160) && r.top > basDisponible;
    setBoite({
      left: r.left,
      width: r.width,
      ...(dessus
        ? { bottom: window.innerHeight - r.top + 4 }
        : { top: r.bottom + 4 }),
    });
  }, []);

  useEffect(() => {
    if (!showDropdown) return;
    choisirPlacement();
    const t = setInterval(choisirPlacement, 400); // le clavier se deplace a la main
    window.addEventListener('resize', choisirPlacement);
    // `true` : le defilement du CORPS du modal ne remonte pas jusqu'a window.
    window.addEventListener('scroll', choisirPlacement, true);
    return () => {
      clearInterval(t);
      window.removeEventListener('resize', choisirPlacement);
      window.removeEventListener('scroll', choisirPlacement, true);
    };
  }, [showDropdown, suggestions.length, choisirPlacement]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Le clavier tactile fait partie de la saisie, pas du « dehors ». Chaque
      // touche emet un mousedown : sans cette garde, la liste se refermait a
      // chaque lettre, et la fermer faisait disparaitre les propositions pour
      // de bon (aucune nouvelle requete ne les ramenait).
      if ((event.target as HTMLElement)?.closest?.('[data-virtual-keyboard]')) return;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  const formatDate = (iso: string | null) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('fr-BE', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (suggestions.length > 0) setShowDropdown(true);
        }}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        autoComplete="off"
        className="w-full border border-gray-300 rounded-lg px-4 py-3 text-lg focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-200 disabled:bg-gray-100"
      />
      {isLoading && (
        <div className="absolute right-3 top-6 -translate-y-1/2">
          <div className="animate-spin h-4 w-4 border-2 border-amber-500 border-t-transparent rounded-full" />
        </div>
      )}

      {/* La liste est dessinee HORS du modal (portail + position fixe).
          Placee en `absolute` dans le corps du modal, elle etait ROGNEE par sa
          zone de defilement des qu'elle remontait au-dessus du champ : elle
          passait sous les etapes « Client / Produits / Date » (signale le
          19/09/2026). Un portail l'affranchit du decoupage, et les coordonnees
          fixes la collent au champ ou qu'il soit. z-index sous le clavier
          (10000) : s'ils se croisent, c'est le clavier qui doit rester dessus. */}
      {showDropdown &&
        suggestions.length > 0 &&
        boite &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: 'fixed',
              left: boite.left,
              width: boite.width,
              ...(boite.top !== undefined ? { top: boite.top } : { bottom: boite.bottom }),
              zIndex: 9999,
            }}
            className="bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto"
          >
            <div className="px-3 py-1.5 text-xs text-gray-500 border-b border-gray-100 bg-gray-50">
              Deja venus — appuyer pour reprendre la fiche
            </div>
            {suggestions.map((c, index) => {
              const last = formatDate(c.last_pickup_date);
              return (
                <button
                  key={`${c.name}-${index}`}
                  type="button"
                  onClick={() => handleSelect(c)}
                  className={`w-full text-left px-4 py-3 min-h-[52px] transition-colors border-b border-gray-50 last:border-b-0 ${
                    index === selectedIndex
                      ? 'bg-amber-50 border-l-4 border-l-amber-500'
                      : 'active:bg-gray-50 hover:bg-gray-50'
                  }`}
                >
                  <div className="font-semibold text-gray-900">{c.name}</div>
                  <div className="text-sm text-gray-500 flex flex-wrap gap-x-3">
                    {c.phone && <span>{c.phone}</span>}
                    <span>
                      {c.orders_count} commande{c.orders_count > 1 ? 's' : ''}
                      {last ? ` · derniere le ${last}` : ''}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
}
